// Simple route charger filtering - uses existing map chargers and filters by route proximity
// This avoids complex API calls and just works with what's already loaded on the map

/**
 * Filters existing map chargers to show only those along the planned route
 * @param {Array} mapChargers - Chargers already loaded and visible on the map
 * @param {Array} routePath - Google Maps route overview_path points
 * @param {number} bufferKm - Distance buffer in kilometers (default 5km)
 * @returns {Array} Filtered chargers along the route
 */
export function filterMapChargersAlongRoute(mapChargers, routePath, bufferKm = 5) {
  if (!Array.isArray(mapChargers) || mapChargers.length === 0) {
    return [];
  }

  if (!Array.isArray(routePath) || routePath.length === 0) {
    return [];
  }


  // Convert route path to simple lat/lng points
  const routePoints = routePath.map(point => ({
    lat: typeof point.lat === 'function' ? point.lat() : point.lat,
    lng: typeof point.lng === 'function' ? point.lng() : point.lng
  }));

  // Haversine distance calculation
  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // Filter chargers within buffer distance of any route point
  const filteredChargers = mapChargers.filter(charger => {
    // Get charger coordinates - handle different field names
    const chargerLat = charger.lat || charger.latitude || charger.locationLatitude;
    const chargerLng = charger.lng || charger.longitude || charger.locationLongitude;

    if (!chargerLat || !chargerLng) {
      return false;
    }

    // Find minimum distance to any point on the route
    let minDistance = Infinity;
    for (const routePoint of routePoints) {
      const distance = calculateDistance(chargerLat, chargerLng, routePoint.lat, routePoint.lng);
      if (distance < minDistance) {
        minDistance = distance;
      }
      // Early exit if within buffer
      if (minDistance <= bufferKm) {
        break;
      }
    }

    // Include charger if within buffer distance
    const isWithinBuffer = minDistance <= bufferKm;
    if (isWithinBuffer) {
      // Add distance info to charger for sorting/display
      charger.distanceFromRoute = Math.round(minDistance * 1000); // meters
    }

    return isWithinBuffer;
  });

  // Sort by distance along route (approximate)
  if (filteredChargers.length > 0 && routePoints.length > 0) {
    const startPoint = routePoints[0];
    filteredChargers.sort((a, b) => {
      const aLat = a.lat || a.latitude || a.locationLatitude;
      const aLng = a.lng || a.longitude || a.locationLongitude;
      const bLat = b.lat || b.latitude || b.locationLatitude;
      const bLng = b.lng || b.longitude || b.locationLongitude;

      const distA = calculateDistance(startPoint.lat, startPoint.lng, aLat, aLng);
      const distB = calculateDistance(startPoint.lat, startPoint.lng, bLat, bLng);
      return distA - distB;
    });
  }

  return filteredChargers;
}

/**
 * Simple function to get route bounds for map fitting
 * @param {Array} routePath - Google Maps route overview_path points
 * @returns {Object} Bounds object with north, south, east, west
 */
export function getRouteBounds(routePath) {
  if (!Array.isArray(routePath) || routePath.length === 0) {
    return null;
  }

  let north = -90, south = 90, east = -180, west = 180;

  routePath.forEach(point => {
    const lat = typeof point.lat === 'function' ? point.lat() : point.lat;
    const lng = typeof point.lng === 'function' ? point.lng() : point.lng;

    if (lat > north) north = lat;
    if (lat < south) south = lat;
    if (lng > east) east = lng;
    if (lng < west) west = lng;
  });

  return { north, south, east, west };
}